import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'src/app/layout/shared/models/menu.model';
import { MeetingType } from 'src/app/pages/projects/models/event.model';
import { AuthenticationService } from './auth.service';
import { SupportStatus, SupportTypes } from 'src/app/pages/support/support.model';
import { AbsenceTypeUnity } from 'src/app/pages/human-resources-list/models/absence-type.model';
import { AbsenceStatus } from 'src/app/pages/human-resources-list/models/absence.model';
import { VacationStatus, VacationType, VacationTypeUnity } from 'src/app/pages/human-resources-list/models/vacation.model';
import { ColaboratorService } from 'src/app/pages/projects/services/colaborators.service';
import { User } from '../models/auth.models';

export enum Genre {
  MALE = "MALE",
  FEMALE = "FEMALE",
  OTHER = "OTHER"
}

export enum Qualifications {
  HIGH_SCHOOL = "HIGH_SCHOOL",
  GRADUATION = "GRADUATION",
  MASTERS = "MASTERS",
  DOCTORATE = "DOCTORATE"
}

export enum TaskGroup {
  All = 'ALL',
  Planning = "PLANNING",
  Work = 'WORK'
}

@Injectable({
  providedIn: 'root'
})
export class DataService {

  currentUser: User | null = null;

  constructor(protected translate: TranslateService,
    protected authSrv: AuthenticationService) {
      this.currentUser = this.authSrv.currentUser;
  }

  /** Color Scheme */
  getColorScheme() {
    //Ngx Ocean
    return [
      '#1D68FB',
      '#33C0FC',
      '#4AFFFE',
      '#AFFFFF',
      '#FFFC63',
      '#FDBD2D',
      '#FC8A25',
      '#FA4F1E',
      '#FA141B',
      '#BA38D1'
    ];

    //Customized
    // return [
    //   '#8BABD8', //Blue dark
    //   '#67BE73', //Green 
    //   '#66FFE9', //Cyan
    //   '#60FF97', //Green Light
    //   '#ffcda1', //Orange Light
    //   '#8e7cc3', //Purple
    //   '#ff9c84', //Red light
    //   '#B5B5B5', //Gray
    //   '#FF92AB', //Pink      
    //   '#ffe599', //Yellow
    // ];
  }

  /** Chart Locales */
  getChartLocales(locale: string) {
    return [{
      name: locale,
      options: {
        months: this.getMonthList(locale),
        shortMonths: this.getMonthList(locale, "short"),
        days: this.getWeekDays(locale),
        shortDays: this.getWeekDays(locale, 'short'),
        toolbar: {
          download: this.translate.instant('CHART.DOWNLOAD'),
          selection: this.translate.instant('CHART.SELECTION'),
          selectionZoom: this.translate.instant('CHART.SELECTIONZOOM'),
          zoomIn: this.translate.instant('CHART.ZOOMIN'),
          zoomOut: this.translate.instant('CHART.ZOOMOUT'),
          pan: this.translate.instant('CHART.PAN'),
          reset: this.translate.instant('CHART.RESET'),
        }
      }
    }];
  }

  getMonthList(locales?: string | string[], format: "long" | "short" = "long"): string[] {
    const year = new Date().getFullYear();
    const monthList = [...Array(12).keys()]; // [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
    const formatter = new Intl.DateTimeFormat(locales, {
      month: format
    });

    const getMonthName = (monthIndex: number) =>
      formatter.format(new Date(year, monthIndex));

    return monthList.map(getMonthName);
  }

  getWeekDays(locales?: string | string[], format: "long" | "short" = "long"): string[] {
    const year = new Date().getFullYear();
    const weekDayList = [...Array(12).keys()]; // [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
    const formatter = new Intl.DateTimeFormat(locales, {
      weekday: format
    });

    const getWeekDayName = (monthIndex: number) =>
      formatter.format(new Date(year, monthIndex));

    return weekDayList.map(getWeekDayName);
  }

  /** Currency */
  getCurrencyISO() {
    return this.getCurrencyOptions().map((obj: any) => obj.value);
  }

  getCurrencyOptions() {
    return [
      { label: this.translate.instant('COIN.EUR'), value: 'EUR' },
      { label: this.translate.instant('COIN.AUD'), value: 'AUD' },
      { label: this.translate.instant('COIN.USD'), value: 'USD' },
      { label: this.translate.instant('COIN.GBP'), value: 'GBP' },
      { label: this.translate.instant('COIN.JPY'), value: 'JPY' },
      { label: this.translate.instant('COIN.CHF'), value: 'CHF' },
      { label: this.translate.instant('COIN.CAD'), value: 'CAD' },
      { label: this.translate.instant('COIN.CNY'), value: 'CNY' },
      { label: this.translate.instant('COIN.ARS'), value: 'ARS' },
      { label: this.translate.instant('COIN.TRL'), value: 'TRL' },
    ];
  }

  /** Meeting Type */
  getMeetingTypes() {
    return [
      { label: this.translate.instant('MEETINGTYPES.PRESENCIAL'), value: MeetingType.PRESENCIAL },
      { label: this.translate.instant('MEETINGTYPES.ONLINE'), value: MeetingType.ONLINE },
    ];
  }

  getAbsenceTypeUnities() {
    return [
      { label: this.translate.instant('ABSENCETYPE.UNITIES.HOUR'), value: AbsenceTypeUnity.HOUR },
      { label: this.translate.instant('ABSENCETYPE.UNITIES.DAY'), value: AbsenceTypeUnity.DAY },
    ];
  }

  getVacationTypes() {
    return [
      { label: this.translate.instant('VACATION.TYPE.HALF_VACATION_DAY'), value: VacationType.HALF_VACATION_DAY },
      { label: this.translate.instant('VACATION.TYPE.VACATION_DAY'), value: VacationType.VACATION_DAY },
      { label: this.translate.instant('VACATION.TYPE.HALF_ADVANCE_DAY'), value: VacationType.HALF_ADVANCE_DAY },
      { label: this.translate.instant('VACATION.TYPE.ADVANCE_DAY'), value: VacationType.ADVANCE_DAY },
    ];
  }

  getAbsenceTypeStatus() {
    return [
      { label: this.translate.instant('ABSENCETYPE.ACTIVE'), value: true },
      { label: this.translate.instant('ABSENCETYPE.DEACTIVE'), value: false }
    ]
  }

  getHolidayStatus() {
    return [
      { label: this.translate.instant('HOLIDAY.ACTIVE'), value: true },
      { label: this.translate.instant('HOLIDAY.DEACTIVE'), value: false }
    ]
  }

  getAbsenceStatus() {
    return [
      { label: this.translate.instant('ABSENCE.STATUS.INSERTED'), value: AbsenceStatus.INSERTED },
      { label: this.translate.instant('ABSENCE.STATUS.APPROVED'), value: AbsenceStatus.APPROVED },
      { label: this.translate.instant('ABSENCE.STATUS.REFUSED'), value: AbsenceStatus.REFUSED },
      { label: this.translate.instant('ABSENCE.STATUS.PENDING'), value: AbsenceStatus.PENDING },
      { label: this.translate.instant('ABSENCE.STATUS.JUSTIFIED'), value: AbsenceStatus.JUSTIFIED }
    ]
  }

  getVacationStatus() {
    return [
      { label: this.translate.instant('VACATION.STATUS.INSERTED'), value: VacationStatus.INSERTED },
      { label: this.translate.instant('VACATION.STATUS.APPROVED'), value: VacationStatus.APPROVED },
      { label: this.translate.instant('VACATION.STATUS.REFUSED'), value: VacationStatus.REFUSED }
    ]
  }

  getGenres() {
    return [
      { label: this.translate.instant('COLABORATOR.ENUMS.GENRE.MALE'), value: Genre.MALE },
      { label: this.translate.instant('COLABORATOR.ENUMS.GENRE.FEMALE'), value: Genre.FEMALE },
      { label: this.translate.instant('COLABORATOR.ENUMS.GENRE.OTHER'), value: Genre.OTHER },
    ]
  }

  getColaboratorPermissions() {
    return [
      { label: this.translate.instant('COLABORATOR.FORM.COLABORATOR'), value: false },
      { label: this.translate.instant('COLABORATOR.FORM.TEAM_MANAGER'), value: true },
    ]
  }

  getQualifications() {
    return [
      { label: this.translate.instant('COLABORATOR.ENUMS.QUALIFICATIONS.HIGH_SCHOOL'), value: Qualifications.HIGH_SCHOOL },
      { label: this.translate.instant('COLABORATOR.ENUMS.QUALIFICATIONS.GRADUATION'), value: Qualifications.GRADUATION },
      { label: this.translate.instant('COLABORATOR.ENUMS.QUALIFICATIONS.MASTERS'), value: Qualifications.MASTERS },
      { label: this.translate.instant('COLABORATOR.ENUMS.QUALIFICATIONS.DOCTORATE'), value: Qualifications.DOCTORATE },
    ]
  }

  /** Country */
  getCountryName(iso: string) {
    return this.getCountryOptions().find((item) => item.value == iso)?.label;
  }
  getCountryISO() {
    return this.getCountryOptions().map((obj: any) => obj.value);
  }

  getCountryOptions() {
    return [
      { label: this.translate.instant('COUNTRY.AF'), value: 'AF' },
      { label: this.translate.instant('COUNTRY.AX'), value: 'AX' },
      { label: this.translate.instant('COUNTRY.AL'), value: 'AL' },
      { label: this.translate.instant('COUNTRY.DZ'), value: 'DZ' },
      { label: this.translate.instant('COUNTRY.AS'), value: 'AS' },
      { label: this.translate.instant('COUNTRY.AD'), value: 'AD' },
      { label: this.translate.instant('COUNTRY.AO'), value: 'AO' },
      { label: this.translate.instant('COUNTRY.AI'), value: 'AI' },
      { label: this.translate.instant('COUNTRY.AQ'), value: 'AQ' },
      { label: this.translate.instant('COUNTRY.AG'), value: 'AG' },
      { label: this.translate.instant('COUNTRY.AR'), value: 'AR' },
      { label: this.translate.instant('COUNTRY.AM'), value: 'AM' },
      { label: this.translate.instant('COUNTRY.AW'), value: 'AW' },
      { label: this.translate.instant('COUNTRY.AU'), value: 'AU' },
      { label: this.translate.instant('COUNTRY.AT'), value: 'AT' },
      { label: this.translate.instant('COUNTRY.AZ'), value: 'AZ' },
      { label: this.translate.instant('COUNTRY.BS'), value: 'BS' },
      { label: this.translate.instant('COUNTRY.BH'), value: 'BH' },
      { label: this.translate.instant('COUNTRY.BD'), value: 'BD' },
      { label: this.translate.instant('COUNTRY.BB'), value: 'BB' },
      { label: this.translate.instant('COUNTRY.BY'), value: 'BY' },
      { label: this.translate.instant('COUNTRY.BE'), value: 'BE' },
      { label: this.translate.instant('COUNTRY.BZ'), value: 'BZ' },
      { label: this.translate.instant('COUNTRY.BJ'), value: 'BJ' },
      { label: this.translate.instant('COUNTRY.BM'), value: 'BM' },
      { label: this.translate.instant('COUNTRY.BT'), value: 'BT' },
      { label: this.translate.instant('COUNTRY.BO'), value: 'BO' },
      { label: this.translate.instant('COUNTRY.BA'), value: 'BA' },
      { label: this.translate.instant('COUNTRY.BW'), value: 'BW' },
      { label: this.translate.instant('COUNTRY.BV'), value: 'BV' },
      { label: this.translate.instant('COUNTRY.BR'), value: 'BR' },
      { label: this.translate.instant('COUNTRY.IO'), value: 'IO' },
      { label: this.translate.instant('COUNTRY.BN'), value: 'BN' },
      { label: this.translate.instant('COUNTRY.BG'), value: 'BG' },
      { label: this.translate.instant('COUNTRY.BF'), value: 'BF' },
      { label: this.translate.instant('COUNTRY.BI'), value: 'BI' },
      { label: this.translate.instant('COUNTRY.KH'), value: 'KH' },
      { label: this.translate.instant('COUNTRY.CM'), value: 'CM' },
      { label: this.translate.instant('COUNTRY.CA'), value: 'CA' },
      { label: this.translate.instant('COUNTRY.CV'), value: 'CV' },
      { label: this.translate.instant('COUNTRY.KY'), value: 'KY' },
      { label: this.translate.instant('COUNTRY.CF'), value: 'CF' },
      { label: this.translate.instant('COUNTRY.TD'), value: 'TD' },
      { label: this.translate.instant('COUNTRY.CL'), value: 'CL' },
      { label: this.translate.instant('COUNTRY.CN'), value: 'CN' },
      { label: this.translate.instant('COUNTRY.CX'), value: 'CX' },
      { label: this.translate.instant('COUNTRY.CC'), value: 'CC' },
      { label: this.translate.instant('COUNTRY.CO'), value: 'CO' },
      { label: this.translate.instant('COUNTRY.KM'), value: 'KM' },
      { label: this.translate.instant('COUNTRY.CG'), value: 'CG' },
      { label: this.translate.instant('COUNTRY.CD'), value: 'CD' },
      { label: this.translate.instant('COUNTRY.CK'), value: 'CK' },
      { label: this.translate.instant('COUNTRY.CR'), value: 'CR' },
      { label: this.translate.instant('COUNTRY.CI'), value: 'CI' },
      { label: this.translate.instant('COUNTRY.HR'), value: 'HR' },
      { label: this.translate.instant('COUNTRY.CU'), value: 'CU' },
      { label: this.translate.instant('COUNTRY.CY'), value: 'CY' },
      { label: this.translate.instant('COUNTRY.CZ'), value: 'CZ' },
      { label: this.translate.instant('COUNTRY.DK'), value: 'DK' },
      { label: this.translate.instant('COUNTRY.DJ'), value: 'DJ' },
      { label: this.translate.instant('COUNTRY.DM'), value: 'DM' },
      { label: this.translate.instant('COUNTRY.DO'), value: 'DO' },
      { label: this.translate.instant('COUNTRY.EC'), value: 'EC' },
      { label: this.translate.instant('COUNTRY.EG'), value: 'EG' },
      { label: this.translate.instant('COUNTRY.SV'), value: 'SV' },
      { label: this.translate.instant('COUNTRY.GQ'), value: 'GQ' },
      { label: this.translate.instant('COUNTRY.ER'), value: 'ER' },
      { label: this.translate.instant('COUNTRY.EE'), value: 'EE' },
      { label: this.translate.instant('COUNTRY.ET'), value: 'ET' },
      { label: this.translate.instant('COUNTRY.FK'), value: 'FK' },
      { label: this.translate.instant('COUNTRY.FO'), value: 'FO' },
      { label: this.translate.instant('COUNTRY.FJ'), value: 'FJ' },
      { label: this.translate.instant('COUNTRY.FI'), value: 'FI' },
      { label: this.translate.instant('COUNTRY.FR'), value: 'FR' },
      { label: this.translate.instant('COUNTRY.GF'), value: 'GF' },
      { label: this.translate.instant('COUNTRY.PF'), value: 'PF' },
      { label: this.translate.instant('COUNTRY.TF'), value: 'TF' },
      { label: this.translate.instant('COUNTRY.GA'), value: 'GA' },
      { label: this.translate.instant('COUNTRY.GM'), value: 'GM' },
      { label: this.translate.instant('COUNTRY.GE'), value: 'GE' },
      { label: this.translate.instant('COUNTRY.DE'), value: 'DE' },
      { label: this.translate.instant('COUNTRY.GH'), value: 'GH' },
      { label: this.translate.instant('COUNTRY.GI'), value: 'GI' },
      { label: this.translate.instant('COUNTRY.GR'), value: 'GR' },
      { label: this.translate.instant('COUNTRY.GL'), value: 'GL' },
      { label: this.translate.instant('COUNTRY.GD'), value: 'GD' },
      { label: this.translate.instant('COUNTRY.GP'), value: 'GP' },
      { label: this.translate.instant('COUNTRY.GU'), value: 'GU' },
      { label: this.translate.instant('COUNTRY.GT'), value: 'GT' },
      { label: this.translate.instant('COUNTRY.GG'), value: 'GG' },
      { label: this.translate.instant('COUNTRY.GN'), value: 'GN' },
      { label: this.translate.instant('COUNTRY.GW'), value: 'GW' },
      { label: this.translate.instant('COUNTRY.GY'), value: 'GY' },
      { label: this.translate.instant('COUNTRY.HT'), value: 'HT' },
      { label: this.translate.instant('COUNTRY.HM'), value: 'HM' },
      { label: this.translate.instant('COUNTRY.VA'), value: 'VA' },
      { label: this.translate.instant('COUNTRY.HN'), value: 'HN' },
      { label: this.translate.instant('COUNTRY.HK'), value: 'HK' },
      { label: this.translate.instant('COUNTRY.HU'), value: 'HU' },
      { label: this.translate.instant('COUNTRY.IS'), value: 'IS' },
      { label: this.translate.instant('COUNTRY.IN'), value: 'IN' },
      { label: this.translate.instant('COUNTRY.ID'), value: 'ID' },
      { label: this.translate.instant('COUNTRY.IR'), value: 'IR' },
      { label: this.translate.instant('COUNTRY.IQ'), value: 'IQ' },
      { label: this.translate.instant('COUNTRY.IE'), value: 'IE' },
      { label: this.translate.instant('COUNTRY.IM'), value: 'IM' },
      { label: this.translate.instant('COUNTRY.IL'), value: 'IL' },
      { label: this.translate.instant('COUNTRY.IT'), value: 'IT' },
      { label: this.translate.instant('COUNTRY.JM'), value: 'JM' },
      { label: this.translate.instant('COUNTRY.JP'), value: 'JP' },
      { label: this.translate.instant('COUNTRY.JE'), value: 'JE' },
      { label: this.translate.instant('COUNTRY.JO'), value: 'JO' },
      { label: this.translate.instant('COUNTRY.KZ'), value: 'KZ' },
      { label: this.translate.instant('COUNTRY.KE'), value: 'KE' },
      { label: this.translate.instant('COUNTRY.KI'), value: 'KI' },
      { label: this.translate.instant('COUNTRY.KP'), value: 'KP' },
      { label: this.translate.instant('COUNTRY.KR'), value: 'KR' },
      { label: this.translate.instant('COUNTRY.KW'), value: 'KW' },
      { label: this.translate.instant('COUNTRY.KG'), value: 'KG' },
      { label: this.translate.instant('COUNTRY.LA'), value: 'LA' },
      { label: this.translate.instant('COUNTRY.LV'), value: 'LV' },
      { label: this.translate.instant('COUNTRY.LB'), value: 'LB' },
      { label: this.translate.instant('COUNTRY.LS'), value: 'LS' },
      { label: this.translate.instant('COUNTRY.LR'), value: 'LR' },
      { label: this.translate.instant('COUNTRY.LY'), value: 'LY' },
      { label: this.translate.instant('COUNTRY.LI'), value: 'LI' },
      { label: this.translate.instant('COUNTRY.LT'), value: 'LT' },
      { label: this.translate.instant('COUNTRY.LU'), value: 'LU' },
      { label: this.translate.instant('COUNTRY.MO'), value: 'MO' },
      { label: this.translate.instant('COUNTRY.MK'), value: 'MK' },
      { label: this.translate.instant('COUNTRY.MG'), value: 'MG' },
      { label: this.translate.instant('COUNTRY.MW'), value: 'MW' },
      { label: this.translate.instant('COUNTRY.MY'), value: 'MY' },
      { label: this.translate.instant('COUNTRY.MV'), value: 'MV' },
      { label: this.translate.instant('COUNTRY.ML'), value: 'ML' },
      { label: this.translate.instant('COUNTRY.MT'), value: 'MT' },
      { label: this.translate.instant('COUNTRY.MH'), value: 'MH' },
      { label: this.translate.instant('COUNTRY.MQ'), value: 'MQ' },
      { label: this.translate.instant('COUNTRY.MR'), value: 'MR' },
      { label: this.translate.instant('COUNTRY.MU'), value: 'MU' },
      { label: this.translate.instant('COUNTRY.YT'), value: 'YT' },
      { label: this.translate.instant('COUNTRY.MX'), value: 'MX' },
      { label: this.translate.instant('COUNTRY.FM'), value: 'FM' },
      { label: this.translate.instant('COUNTRY.MD'), value: 'MD' },
      { label: this.translate.instant('COUNTRY.MC'), value: 'MC' },
      { label: this.translate.instant('COUNTRY.MN'), value: 'MN' },
      { label: this.translate.instant('COUNTRY.MS'), value: 'MS' },
      { label: this.translate.instant('COUNTRY.MA'), value: 'MA' },
      { label: this.translate.instant('COUNTRY.MZ'), value: 'MZ' },
      { label: this.translate.instant('COUNTRY.MM'), value: 'MM' },
      { label: this.translate.instant('COUNTRY.NA'), value: 'NA' },
      { label: this.translate.instant('COUNTRY.NR'), value: 'NR' },
      { label: this.translate.instant('COUNTRY.NP'), value: 'NP' },
      { label: this.translate.instant('COUNTRY.NL'), value: 'NL' },
      { label: this.translate.instant('COUNTRY.AN'), value: 'AN' },
      { label: this.translate.instant('COUNTRY.NC'), value: 'NC' },
      { label: this.translate.instant('COUNTRY.NZ'), value: 'NZ' },
      { label: this.translate.instant('COUNTRY.NI'), value: 'NI' },
      { label: this.translate.instant('COUNTRY.NE'), value: 'NE' },
      { label: this.translate.instant('COUNTRY.NG'), value: 'NG' },
      { label: this.translate.instant('COUNTRY.NU'), value: 'NU' },
      { label: this.translate.instant('COUNTRY.NF'), value: 'NF' },
      { label: this.translate.instant('COUNTRY.MP'), value: 'MP' },
      { label: this.translate.instant('COUNTRY.NO'), value: 'NO' },
      { label: this.translate.instant('COUNTRY.OM'), value: 'OM' },
      { label: this.translate.instant('COUNTRY.PK'), value: 'PK' },
      { label: this.translate.instant('COUNTRY.PW'), value: 'PW' },
      { label: this.translate.instant('COUNTRY.PS'), value: 'PS' },
      { label: this.translate.instant('COUNTRY.PA'), value: 'PA' },
      { label: this.translate.instant('COUNTRY.PG'), value: 'PG' },
      { label: this.translate.instant('COUNTRY.PY'), value: 'PY' },
      { label: this.translate.instant('COUNTRY.PE'), value: 'PE' },
      { label: this.translate.instant('COUNTRY.PH'), value: 'PH' },
      { label: this.translate.instant('COUNTRY.PN'), value: 'PN' },
      { label: this.translate.instant('COUNTRY.PL'), value: 'PL' },
      { label: this.translate.instant('COUNTRY.PT'), value: 'PT' },
      { label: this.translate.instant('COUNTRY.PR'), value: 'PR' },
      { label: this.translate.instant('COUNTRY.QA'), value: 'QA' },
      { label: this.translate.instant('COUNTRY.RE'), value: 'RE' },
      { label: this.translate.instant('COUNTRY.RO'), value: 'RO' },
      { label: this.translate.instant('COUNTRY.RU'), value: 'RU' },
      { label: this.translate.instant('COUNTRY.RW'), value: 'RW' },
      { label: this.translate.instant('COUNTRY.SH'), value: 'SH' },
      { label: this.translate.instant('COUNTRY.KN'), value: 'KN' },
      { label: this.translate.instant('COUNTRY.LC'), value: 'LC' },
      { label: this.translate.instant('COUNTRY.PM'), value: 'PM' },
      { label: this.translate.instant('COUNTRY.VC'), value: 'VC' },
      { label: this.translate.instant('COUNTRY.WS'), value: 'WS' },
      { label: this.translate.instant('COUNTRY.SM'), value: 'SM' },
      { label: this.translate.instant('COUNTRY.ST'), value: 'ST' },
      { label: this.translate.instant('COUNTRY.SA'), value: 'SA' },
      { label: this.translate.instant('COUNTRY.SN'), value: 'SN' },
      { label: this.translate.instant('COUNTRY.CS'), value: 'CS' },
      { label: this.translate.instant('COUNTRY.SC'), value: 'SC' },
      { label: this.translate.instant('COUNTRY.SL'), value: 'SL' },
      { label: this.translate.instant('COUNTRY.SG'), value: 'SG' },
      { label: this.translate.instant('COUNTRY.SK'), value: 'SK' },
      { label: this.translate.instant('COUNTRY.SI'), value: 'SI' },
      { label: this.translate.instant('COUNTRY.SB'), value: 'SB' },
      { label: this.translate.instant('COUNTRY.SO'), value: 'SO' },
      { label: this.translate.instant('COUNTRY.ZA'), value: 'ZA' },
      { label: this.translate.instant('COUNTRY.GS'), value: 'GS' },
      { label: this.translate.instant('COUNTRY.ES'), value: 'ES' },
      { label: this.translate.instant('COUNTRY.LK'), value: 'LK' },
      { label: this.translate.instant('COUNTRY.SD'), value: 'SD' },
      { label: this.translate.instant('COUNTRY.SR'), value: 'SR' },
      { label: this.translate.instant('COUNTRY.SJ'), value: 'SJ' },
      { label: this.translate.instant('COUNTRY.SZ'), value: 'SZ' },
      { label: this.translate.instant('COUNTRY.SE'), value: 'SE' },
      { label: this.translate.instant('COUNTRY.CH'), value: 'CH' },
      { label: this.translate.instant('COUNTRY.SY'), value: 'SY' },
      { label: this.translate.instant('COUNTRY.TW'), value: 'TW' },
      { label: this.translate.instant('COUNTRY.TJ'), value: 'TJ' },
      { label: this.translate.instant('COUNTRY.TZ'), value: 'TZ' },
      { label: this.translate.instant('COUNTRY.TH'), value: 'TH' },
      { label: this.translate.instant('COUNTRY.TL'), value: 'TL' },
      { label: this.translate.instant('COUNTRY.TG'), value: 'TG' },
      { label: this.translate.instant('COUNTRY.TK'), value: 'TK' },
      { label: this.translate.instant('COUNTRY.TO'), value: 'TO' },
      { label: this.translate.instant('COUNTRY.TT'), value: 'TT' },
      { label: this.translate.instant('COUNTRY.TN'), value: 'TN' },
      { label: this.translate.instant('COUNTRY.TR'), value: 'TR' },
      { label: this.translate.instant('COUNTRY.TM'), value: 'TM' },
      { label: this.translate.instant('COUNTRY.TC'), value: 'TC' },
      { label: this.translate.instant('COUNTRY.TV'), value: 'TV' },
      { label: this.translate.instant('COUNTRY.UG'), value: 'UG' },
      { label: this.translate.instant('COUNTRY.UA'), value: 'UA' },
      { label: this.translate.instant('COUNTRY.AE'), value: 'AE' },
      { label: this.translate.instant('COUNTRY.GB'), value: 'GB' },
      { label: this.translate.instant('COUNTRY.US'), value: 'US' },
      { label: this.translate.instant('COUNTRY.UM'), value: 'UM' },
      { label: this.translate.instant('COUNTRY.UY'), value: 'UY' },
      { label: this.translate.instant('COUNTRY.UZ'), value: 'UZ' },
      { label: this.translate.instant('COUNTRY.VU'), value: 'VU' },
      { label: this.translate.instant('COUNTRY.VE'), value: 'VE' },
      { label: this.translate.instant('COUNTRY.VN'), value: 'VN' },
      { label: this.translate.instant('COUNTRY.VG'), value: 'VG' },
      { label: this.translate.instant('COUNTRY.VI'), value: 'VI' },
      { label: this.translate.instant('COUNTRY.WF'), value: 'WF' },
      { label: this.translate.instant('COUNTRY.EH'), value: 'EH' },
      { label: this.translate.instant('COUNTRY.YE'), value: 'YE' },
      { label: this.translate.instant('COUNTRY.ZM'), value: 'ZM' },
      { label: this.translate.instant('COUNTRY.ZW'), value: 'ZW' }
    ];
  }

  /** Task Columns */
  getTaskCols() {
    return [
      { group: TaskGroup.Planning, style: 'min-width: 200px; max-width: 300px', field: 'requirement', header: this.translate.instant('BOARD.FORM.REQUIREMENT'), sort: 'requirement.requirement' },
      { group: TaskGroup.All, style: 'min-width: 200px; max-width: 300px', field: 'title', header: this.translate.instant('BOARD.FORM.TITLE'), sort: 'title' },
      { group: TaskGroup.All, style: 'min-width: 200px; max-width: 400px', field: 'description', header: this.translate.instant('BOARD.FORM.DESCRIPTION'), sort: 'description', type: 'description' },
      { group: TaskGroup.Work, style: 'min-width: 200px; max-width: 400px', field: 'impediments', header: this.translate.instant('BOARD.FORM.IMPEDIMENTS'), sort: 'impediments', type: 'description' },
      { group: TaskGroup.All, style: 'min-width: 200px; max-width: 400px', field: 'notes', header: this.translate.instant('BOARD.FORM.NOTES'), sort: 'notes', type: 'description' },
      { group: TaskGroup.All, style: 'min-width: 100px; max-width: 200px', field: 'tags', header: this.translate.instant('BOARD.FORM.AREA'), sort: 'tags' },
      { group: TaskGroup.All, style: 'min-width: 100px; max-width: 200px', field: 'column', header: this.translate.instant('BOARD.FORM.SCRUMCOLUMN'), sort: 'column.name' },
      { group: TaskGroup.Planning, style: 'min-width: 100px; max-width: 200px', field: 'priority', header: this.translate.instant('BOARD.FORM.PRIORITY'), sort: 'priority.description' },
      { group: TaskGroup.Planning, style: 'min-width: 100px; max-width: 200px', field: 'type', header: this.translate.instant('BOARD.FORM.TYPE'), sort: 'type.description' },
      { group: TaskGroup.Planning, style: 'min-width: 150px; max-width: 200px', field: 'sprint', header: this.translate.instant('BOARD.FORM.SPRINT'), sort: 'sprint.name' },
      { group: TaskGroup.Planning, style: 'min-width: 80px; max-width: 80px', field: 'planned', header: this.translate.instant('BOARD.FORM.PLANNEDTASK'), sort: 'plannedTask', type: 'status' },
      { group: TaskGroup.Planning, style: 'min-width: 150px; max-width: 200px', field: 'assigned', header: this.translate.instant('BOARD.FORM.ASSIGNEDTO'), sort: 'assigned.firstName,assigned.lastName' },
      { group: TaskGroup.Planning, style: 'min-width: 150px; max-width: 200px', field: 'requestedBy', header: this.translate.instant('BOARD.FORM.REQUESTEDBY'), sort: 'requestedBy' },
      { group: TaskGroup.All, style: 'min-width: 120px; max-width: 120px', field: 'startDate', header: this.translate.instant('BOARD.FORM.STARTDATE'), sort: 'startDate', type: 'date' },
      { group: TaskGroup.All, style: 'min-width: 120px; max-width: 120px', field: 'endDate', header: this.translate.instant('BOARD.FORM.ENDDATE'), sort: 'endDate', type: 'date' },
      { group: TaskGroup.Planning, style: 'min-width: 80px; max-width: 100px', field: 'estimatedDuration', header: this.translate.instant('BOARD.FORM.ESTIMATEDDURATIOM'), sort: 'estimatedDuration', type: 'number' },
      { group: TaskGroup.All, admin: true, style: 'min-width: 100px; max-width: 150px', field: 'estimatedCost', header: this.translate.instant('BOARD.FORM.ESTIMATEDCOST'), sort: 'estimatedCost', type: 'number' },
      { group: TaskGroup.All, admin: true, style: 'min-width: 100px; max-width: 150px', field: 'realCost', header: this.translate.instant('BOARD.FORM.REALCOST'), sort: 'realCost', type: 'number' },
      { group: TaskGroup.All, style: 'min-width: 50px; max-width: 50px', field: 'files', header: this.translate.instant('MISC.FILES'), type: 'file' },
    ];
  }

  /** Translation */
  getRoleTranslationKey(role: string) {
    switch (role) {
      case "ADMIN":
        return "MISC.ADMIN";
      case "MEMBER":
        return "MISC.MEMBER";
      case "PARTNER":
        return "MISC.PARTNER";
      case "CUSTOMER":
        return "MISC.CUSTOMER";
      default:
        return "MISC.NOT_FOUND";
    }
  }

  getPermissionTranslationKey(role: string) {
    if (role.includes(':create')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.CREATE';
    } else if (role.includes(':revision:read')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REVISIONINFO';
    } else if (role.includes(':revision')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REVISION';
    } else if (role.includes(':read')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.READ';
    } else if (role.includes(':update')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.UPDATE';
    } else if (role.includes(':delete')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.DELETE';
    } else if (role.includes(':submit')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SUBMIT';
    } else if (role.includes(':approve')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.APPROVE';
    } else if (role.includes(':reject')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REJECT';
    } else if (role.includes(':activate')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.ACTIVATE';
    } else if (role.includes(':finish')) {
      return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.FINISH';
    } else {
      switch (role) {
        case 'users': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.USERS';
        case 'partners': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.PARTNERS';
        case 'customers': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.CUSTOMERS';
        case 'subroles': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SUBROLES';
        case 'supports': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SUPPORTS';
        case 'reports': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REPORTS';
        case 'sales': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SALES';
        case 'sales:requests': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REQUESTS';
        case 'sales:requirements': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.REQUIREMENTS';
        case 'sales:budgets': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.BUDGETS';
        case 'sales:proposals': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.PROPOSALS';
        case 'sales:contracts': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.CONTRACTS';
        case 'sales:portfolio': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.PORTFOLIO';
        case 'humanresources': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.HUMANRESOURCES';
        case 'humanresources:absences': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.ABSENCES';
        case 'humanresources:vacations': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.VACATIONS';
        case 'humanresources:hrsettings': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.HRSETTINGS';
        case 'projects': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.PROJECTS';
        case 'projects:resources': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.RESOURCES';
        case 'projects:resources:teams': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.TEAMS';
        case 'projects:resources:material': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.MATERIALS';
        case 'projects:resources:trips': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.TRIPS';
        case 'projects:board': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.BOARD';
        case 'projects:board:tasks': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.TASKS';
        case 'projects:board:sprints': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SPRINTS';
        case 'projects:dashboard': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.DASHBOARD';
        case 'projects:calendar': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.CALENDAR';
        case 'projects:wikis': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.WIKIS';
        case 'projects:settings': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.PSETTINGS';
        case 'projects:artifacts': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.ARTIFACTS';
        case 'projects:meetings': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.MEETINGS';
        case 'projects:versions': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.VERSIONS';
        case 'projects:bugs': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.BUGS';
        case 'settings': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.SETTINGS';
        default: return "MISC.NOT_FOUND";
      }
    }
  }
  getNotificationTranslationKey(role: string) {
    if (role.includes(':email')) {
      return 'SETTINGS.NOTIFICATIONS.OPTION.EMAIL';
    } else if (role.includes(':notification')) {
      return 'SETTINGS.NOTIFICATIONS.OPTION.NOTIFICATION';
    } else {
      switch (role) {
        case 'users': return 'SETTINGS.SUBROLES.FORM.PERMISSIONS.USERS';
        case 'users:permissionsUpdate': return 'SETTINGS.NOTIFICATIONS.USERS.PERMISSIONUPDATE';
        case 'users:update': return 'SETTINGS.NOTIFICATIONS.USERS.UPDATE';
        case 'meetings': return 'SETTINGS.NOTIFICATIONS.MEETINGS.MEETINGS';
        case 'meetings:new': return 'SETTINGS.NOTIFICATIONS.MEETINGS.NEW';
        case 'companies': return 'SETTINGS.NOTIFICATIONS.COMPANIES.COMPANIES';
        case 'companies:newUser': return 'SETTINGS.NOTIFICATIONS.COMPANIES.NEWUSER';
        case 'companies:update': return 'SETTINGS.NOTIFICATIONS.COMPANIES.UPDATE';
        case 'supports': return 'SETTINGS.NOTIFICATIONS.SUPPORTS.SUPPORTS';
        case 'supports:new': return 'SETTINGS.NOTIFICATIONS.SUPPORTS.NEW';
        case 'supports:assign': return 'SETTINGS.NOTIFICATIONS.SUPPORTS.ASSIGN';
        case 'supports:newTask': return 'SETTINGS.NOTIFICATIONS.SUPPORTS.NEWTASK';
        case 'supports:message': return 'SETTINGS.NOTIFICATIONS.SUPPORTS.MESSAGE';
        case 'projects': return 'SETTINGS.NOTIFICATIONS.PROJECTS.PROJECTS';
        case 'projects:update': return 'SETTINGS.NOTIFICATIONS.PROJECTS.UPDATE';
        case 'projects:tasks': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.TASKS';
        case 'projects:tasks:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.NEW';
        case 'projects:tasks:done': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.DONE';
        case 'projects:tasks:assigned': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.ASSIGNED';
        case 'projects:tasks:update': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.UPDATE';
        case 'projects:tasks:start': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.START';
        case 'projects:tasks:stop': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.STOP';
        case 'projects:tasks:tag': return 'SETTINGS.NOTIFICATIONS.PROJECTS.TASKS.TAG';
        case 'projects:sprints': return 'SETTINGS.NOTIFICATIONS.PROJECTS.SPRINTS.SPRINTS';
        case 'projects:sprints:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.SPRINTS.NEW';
        case 'projects:sprints:over': return 'SETTINGS.NOTIFICATIONS.PROJECTS.SPRINTS.OVER';
        case 'projects:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.NEW';
        case 'projects:over': return 'SETTINGS.NOTIFICATIONS.PROJECTS.OVER';
        case 'projects:resources': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.RESOURCES';
        case 'projects:resources:teams': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.TEAMS.TEAMS';
        case 'projects:resources:teams:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.TEAMS.NEW';
        case 'projects:resources:teams:assigned': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.TEAMS.ASSIGNED';
        case 'projects:resources:equipments': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.EQUIPMENTS.EQUIPMENTS';
        case 'projects:resources:equipments:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.EQUIPMENTS.NEW';
        case 'projects:resources:trips': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.TRIPS.TRIPS';
        case 'projects:resources:trips:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.RESOURCES.TRIPS.NEW';
        case 'projects:meetings': return 'SETTINGS.NOTIFICATIONS.PROJECTS.MEETINGS.MEETINGS';
        case 'projects:meetings:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.MEETINGS.NEW';
        case 'projects:wikis': return 'SETTINGS.NOTIFICATIONS.PROJECTS.WIKIS.WIKIS';
        case 'projects:wikis:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.WIKIS.NEW';
        case 'projects:bugs': return 'SETTINGS.NOTIFICATIONS.PROJECTS.BUGS.BUGS';
        case 'projects:bugs:new': return 'SETTINGS.NOTIFICATIONS.PROJECTS.BUGS.NEW';
        case 'projects:bugs:update': return 'SETTINGS.NOTIFICATIONS.PROJECTS.BUGS.UPDATE';
        case 'projects:bugs:newTask': return 'SETTINGS.NOTIFICATIONS.PROJECTS.BUGS.NEWTASK';
        case 'humanresources': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.HUMANRESOURCES';
        case 'humanresources:absences': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.ABSENCES';
        case 'humanresources:absences:create': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.NEW';
        case 'humanresources:absences:update': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.UPDATE';
        case 'humanresources:absences:delete': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.DELETE';
        case 'humanresources:absences:manage': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.MANAGE.MANAGE';
        case 'humanresources:absences:manage:accept': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.MANAGE.ACCEPTED';
        case 'humanresources:absences:manage:refuse': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.MANAGE.REFUSED';
        case 'humanresources:absences:manage:revert': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.ABSENCES.MANAGE.REVERTED';
        case 'humanresources:vacations': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.VACATIONS';
        case 'humanresources:vacations:create': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.NEW';
        case 'humanresources:vacations:update': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.UPDATE';
        case 'humanresources:vacations:delete': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.DELETE';
        case 'humanresources:vacations:manage': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.MANAGE.MANAGE';
        case 'humanresources:vacations:manage:accept': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.MANAGE.ACCEPTED';
        case 'humanresources:vacations:manage:refuse': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.MANAGE.REFUSED';
        case 'humanresources:vacations:manage:revert': return 'SETTINGS.NOTIFICATIONS.HUMANRESOURCES.VACATIONS.MANAGE.REVERTED';
        default: return "MISC.NOT_FOUND";
      }
    }
  }

  getSprintChartTranslateKey(type: string) {
    switch (type) {
      case "TODO":
        return "DASHBOARD.SPRINT.TODO";
      case "PROGRESS":
        return "DASHBOARD.SPRINT.PROGRESS";
      case "DONE":
        return "DASHBOARD.SPRINT.DONE";
      default:
        return "DASHBOARD.SPRINT.BACKLOG";
    }
  }

  /** Menu */
  getMenu(): MenuItem[] {
    const menu: MenuItem[] = [
      {
        key: 'home',
        label: 'SIDEBAR.HOME',
        isTitle: false,
        icon: 'mdi mdi-home',
        link: '/home',
      },
      /* {
        key: 'mycalendar',
        label: 'SIDEBAR.MYCALENDAR',
        isTitle: false,
        icon: 'mdi mdi-calendar-month',
        link: '/mycalendar',
      }, */

      {
        key: 'pending',
        label: 'Pendentes',
        isTitle: false,
        icon: 'mdi mdi-timer-sand',
        link: '/tickets/pending',
      },
      {
        key: 'scheduled',
        label: 'Agendamentos',
        isTitle: false,
        icon: 'mdi mdi-timer-outline',
        link: '/tickets/scheduled',
      },
      {
        key: 'tasks',
        label: 'Tarefas',
        isTitle: false,
        icon: 'mdi mdi-note-check-outline',
        link: '/tickets/tasks',
      },
      {
        key: 'general',
        label: 'Tickets',
        isTitle: false,
        icon: 'mdi mdi-ticket',
        link: '/tickets/all',
      },
      {
        key: 'calendar',
        label: 'Calendário',
        isTitle: false,
        icon: 'mdi mdi-calendar-month',
        link: '/tickets/calendar',
      },
      {
        key: 'weekResume',
        label: 'Resumo Semanal',
        isTitle: false,
        icon: 'mdi mdi-chart-bar',
        link: '/tickets/weekresume',
      },
    ];

    if (this.authSrv.currentUser?.admin) {
      menu.push({
        key: 'budgets',
        label: 'Orçamentos Clientes',
        isTitle: false,
        icon: 'mdi mdi-calculator-variant-outline',
        link: '/tickets/budgets',
      },
        {
          key: 'client_orders',
          label: 'Encomendas Clientes',
          isTitle: false,
          icon: 'mdi mdi-clipboard-list-outline',
          link: '/tickets/client_orders',
        },
        {
          key: 'supplier_orders',
          label: 'Encomendas Fornecedores',
          isTitle: false,
          icon: 'mdi mdi-package-variant-closed',
          link: '/tickets/supplier_orders',
        },
        {
          key: 'contracts',
          label: 'Contratos',
          isTitle: false,
          icon: 'mdi mdi-file-document-outline',
          link: '/tickets/contracts',
        }
        // {
        //     key: 'dashboard',
        //     label: 'Dashboard',
        //     isTitle: false,
        //     icon: 'mdi mdi-chart-bar',
        //     link: '/tickets/dashboard',
        // }
      );
      // {
      //   key: 'powerbi',
      //   label: 'Dashboard',
      //   isTitle: false,
      //   icon: 'mdi mdi-view-dashboard',
      //   link: '/tickets/dashboard',
      // });
    }

    menu.push({
      key: 'changePassword',
      label: 'Alterar Password',
      isTitle: false,
      icon: 'mdi mdi-lock',
      link: '/profile',
    });

    /* if (this.authSrv.hasPermission('sales')) {
      const salesChildren = [];

      if (this.authSrv.hasPermission('sales:requests')) {
        salesChildren.push({
          key: 'requests',
          label: 'SIDEBAR.SALES.REQUESTS',
          icon: 'mdi mdi-application',
          link: '/sales/requests',
          parentKey: 'sales'
        });
      }

      if (!this.authSrv.currentUser?.isCustomer && this.authSrv.hasPermission('sales:budgets')) {
        salesChildren.push({
          key: 'budgets',
          label: 'SIDEBAR.SALES.BUDGETS',
          icon: 'mdi mdi-file-table',
          link: '/sales/budgets',
          parentKey: 'sales',
        });
      }

      if (this.authSrv.hasPermission('sales:proposals')) {
        salesChildren.push({
          key: 'proposals',
          label: 'SIDEBAR.SALES.PROPOSALS',
          icon: 'mdi mdi-file-document',
          link: '/sales/proposals',
          parentKey: 'sales',
        });
      }

      if (this.authSrv.hasPermission('sales:contracts')) {
        salesChildren.push({
          key: 'contracts',
          label: 'SIDEBAR.SALES.CONTRACTS',
          icon: 'mdi mdi-file-sign',
          link: '/sales/contracts',
          parentKey: 'sales',
        });
      }

      if (this.authSrv.hasPermission('sales:portfolio')) {
        salesChildren.push({
          key: 'portfolio',
          label: 'SIDEBAR.SALES.PORTFOLIO',
          icon: 'mdi mdi-archive',
          link: '/sales/portfolio',
          parentKey: 'sales',
        });
      }

      menu.push({
        key: 'sales',
        label: 'SIDEBAR.SALES.SALES',
        isTitle: false,
        icon: 'mdi mdi-cart-plus',
        collapsed: true,
        children: salesChildren
      });
    } */

    /* if (this.authSrv.hasPermission('projects')) {
      menu.push({
        key: 'projects',
        label: 'SIDEBAR.PROJECTS',
        isTitle: false,
        icon: 'mdi mdi-application-brackets',
        link: '/projects/list'
      });
    } */

    /* if (this.authSrv.hasPermission('humanresources')) {
      const humanResourcesChildren = [];

      if (this.authSrv.hasPermission('humanresources:absences')) {
        humanResourcesChildren.push({
          key: 'absences',
          label: 'SIDEBAR.HR.ABSENCES',
          icon: 'mdi mdi-clipboard-edit-outline',
          link: '/humanresources/absences',
          parentKey: 'humanresources'
        });
      }

      if (this.authSrv.hasPermission('humanresources:vacations')) {
        humanResourcesChildren.push({
          key: 'vacations',
          label: 'SIDEBAR.HR.VACATIONS',
          icon: 'mdi mdi-text-box-check-outline',
          link: '/humanresources/vacations',
          parentKey: 'humanresources'
        });
      }

      if (this.authSrv.hasPermission('humanresources:hrsettings') || this.authSrv.currentColaborator.teamManager) {
        humanResourcesChildren.push({
          key: 'hrsettings',
          label: 'SIDEBAR.HR.SETTINGS',
          icon: 'mdi mdi-cogs',
          link: '/humanresources/hrsettings',
          parentKey: 'humanresources'
        });
      }

      humanResourcesChildren.push({
        key: 'colaborators',
        label: 'SIDEBAR.HR.COLABORATORS',
        icon: 'mdi mdi-account-group',
        link: '/humanresources/colaborators',
        parentKey: 'humanresources'
      });

      menu.push({
        key: 'humanresources',
        label: 'SIDEBAR.HR.HR',
        isTitle: false,
        icon: 'mdi mdi-badge-account-horizontal-outline',
        collapsed: true,
        children: humanResourcesChildren
      });
    } */

    /* if (this.authSrv.checkPermissions(['users', 'partners', 'customers'])) {
      menu.push({
        key: 'users',
        label: 'SIDEBAR.USERS',
        isTitle: false,
        icon: 'mdi mdi-account-group',
        link: '/users',
      });
    } */

    /* if (this.authSrv.checkPermissions(['partners', 'customers'])) {
      menu.push({
        key: 'companies',
        label: this.authSrv.currentUser?.isPartner ? 'SIDEBAR.CUSTOMERS' : 'SIDEBAR.COMPANIES',
        isTitle: false,
        icon: 'mdi mdi-office-building',
        link: '/companies'
      });
    }

    if (this.authSrv.hasPermission('reports')) {
      menu.push({
        key: 'reports',
        label: 'SIDEBAR.REPORTS',
        isTitle: false,
        icon: 'mdi mdi-file-chart',
        link: '/reports',
        disabled: true
      });
    } */

    if (this.authSrv.hasPermission('supports')) {
      menu.push({
        key: 'supports',
        label: 'SIDEBAR.SUPPORT',
        isTitle: false,
        icon: 'mdi mdi-information',
        link: '/supports',
      });
    }

    if (this.authSrv.hasPermission('settings')) {
      menu.push({
        key: 'settings',
        label: 'SIDEBAR.SETTINGS',
        isTitle: false,
        icon: 'mdi mdi-cogs',
        link: '/settings',
      });
    }

    return menu;

  }

  getProjectMenu(projectId: number, projectName: string): MenuItem[] {
    const menu: MenuItem[] = [];

    if (this.authSrv.hasPermission('projects')) {
      menu.push({
        key: 'projects',
        label: 'PROJECTS.SIDEBAR.PROJECTLIST',
        isTitle: false,
        icon: 'mdi mdi-arrow-left',
        link: '/projects/list',
      });
    }

    if (this.authSrv.hasPermission('projects:read')) {
      menu.push({
        key: 'project',
        label: projectName,
        isTitle: false,
        icon: 'mdi mdi-application-brackets',
        link: `/projects/${projectId}/project`
      });
    }

    if (this.authSrv.hasPermission('projects:dashboard')) {
      menu.push({
        key: 'Dashboard',
        label: 'PROJECTS.SIDEBAR.DASHBOARD',
        isTitle: false,
        icon: 'mdi mdi-view-dashboard',
        link: `/projects/${projectId}/dashboard`
      });
    }

    if (this.authSrv.hasPermission('projects:artifacts')) {
      menu.push({
        key: 'Artifacts',
        label: 'PROJECTS.SIDEBAR.ARTIFACTS',
        isTitle: false,
        icon: 'mdi mdi-pencil-ruler',
        link: `/projects/${projectId}/artifacts`
      });
    }

    if (this.authSrv.hasPermission('projects:resources')) {
      menu.push({
        key: 'Resources',
        label: 'PROJECTS.SIDEBAR.RESOURCES',
        isTitle: false,
        icon: 'mdi mdi-cogs',
        link: `/projects/${projectId}/resources`
      });
    }

    if (this.authSrv.hasPermission('projects:calendar')) {
      menu.push({
        key: 'Calendar',
        label: 'PROJECTS.SIDEBAR.CALENDAR',
        isTitle: false,
        icon: 'mdi mdi-calendar-month',
        link: `/projects/${projectId}/calendar`
      });
    }

    if (this.authSrv.hasPermission('projects:board')) {
      menu.push({
        key: 'Board',
        label: 'PROJECTS.SIDEBAR.BOARD',
        isTitle: false,
        icon: 'mdi mdi-view-week',
        link: `/projects/${projectId}/board`
      });
    }

    if (this.authSrv.hasPermission('projects:meetings')) {
      menu.push({
        key: 'Meetings',
        label: 'PROJECTS.SIDEBAR.MEETING',
        isTitle: false,
        icon: 'mdi mdi-wechat',
        link: `/projects/${projectId}/meetings`
      });
    }

    if (this.authSrv.hasPermission('projects:wikis')) {
      menu.push({
        key: 'Wiki',
        label: 'PROJECTS.SIDEBAR.WIKI',
        isTitle: false,
        icon: 'mdi mdi-book-open-variant',
        link: `/projects/${projectId}/wiki`
      });
    }

    if (this.authSrv.hasPermission('projects:versions')) {
      menu.push({
        key: 'Version',
        label: 'PROJECTS.SIDEBAR.VERSION',
        isTitle: false,
        icon: 'mdi mdi-share-variant',
        link: `/projects/${projectId}/versions`,
        disabled: true
      });
    }

    if (this.authSrv.hasPermission('projects:bugs')) {
      menu.push({
        key: 'Bug',
        label: 'PROJECTS.SIDEBAR.BUGTRACKING',
        isTitle: false,
        icon: 'mdi mdi-bug',
        link: `/projects/${projectId}/bugs`,
      });
    }

    if (this.authSrv.hasPermission('projects:settings')) {
      menu.push({
        key: 'settingsP',
        label: 'PROJECTS.SIDEBAR.SETTINGS',
        isTitle: false,
        icon: 'mdi mdi-application-cog',
        link: `/projects/${projectId}/settings`
      });
    }

    return menu;
  }

  /*SUPPORT */

  getSupportStatus(value: SupportStatus) {
    switch (value) {
      case SupportStatus.NEW:
        return this.translate.instant('SUPPORT.STATUS.NEW');
      case SupportStatus.CLOSED:
        return this.translate.instant('SUPPORT.STATUS.CLOSED');
      case SupportStatus.PENDING:
        return this.translate.instant('SUPPORT.STATUS.PENDING');
      case SupportStatus.PROGRESS:
        return this.translate.instant('SUPPORT.STATUS.PROGRESS');
      case SupportStatus.SOLVED:
        return this.translate.instant('SUPPORT.STATUS.SOLVED');
    }
  }
  getSupportType(value: SupportTypes) {
    switch (value) {
      case SupportTypes.INCIDENT:
        return this.translate.instant('SUPPORT.TYPES.INCIDENT');
      case SupportTypes.REQUEST:
        return this.translate.instant('SUPPORT.TYPES.REQUEST');
    }
  }

  getStatusSupportList() {
    return [
      { label: this.translate.instant('SUPPORT.STATUS.NEW'), value: SupportStatus.NEW },
      { label: this.translate.instant('SUPPORT.STATUS.CLOSED'), value: SupportStatus.CLOSED },
      { label: this.translate.instant('SUPPORT.STATUS.PENDING'), value: SupportStatus.PENDING },
      { label: this.translate.instant('SUPPORT.STATUS.PROGRESS'), value: SupportStatus.PROGRESS },
      { label: this.translate.instant('SUPPORT.STATUS.SOLVED'), value: SupportStatus.SOLVED },
    ];
  }
  getTypesSupportList() {
    return [
      { label: this.translate.instant('SUPPORT.TYPES.INCIDENT'), value: SupportTypes.INCIDENT },
      { label: this.translate.instant('SUPPORT.TYPES.REQUEST'), value: SupportTypes.REQUEST },
    ]
  }
}
